import React, { useState, useEffect } from "react";
import { db } from "../../services/firebase";
import { collection, addDoc } from "firebase/firestore";
import { sendEnquiryNotification } from "../../services/emailService";

// Closer / LeadConnector embedded form. The form is defined in the Closer
// dashboard, so its fields map directly to the correct contact fields there.
// Default Sidcup Closer form id (preserves existing live behaviour).
const DEFAULT_CLOSER_FORM_ID = "d13dUfgth2CbmYYiah68";

const CloserContactForm = ({ formId }) => {
	const closerFormId = formId || DEFAULT_CLOSER_FORM_ID;
	useEffect(() => {
		// Load the LeadConnector embed script once so the iframe auto-resizes.
		const SCRIPT_SRC = "https://link.msgsndr.com/js/form_embed.js";
		if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
			return;
		}
		const script = document.createElement("script");
		script.src = SCRIPT_SRC;
		script.async = true;
		document.body.appendChild(script);
	}, []);

	return (
		<section id="contact" className="contact-section">
			<div className="container">
				<div className="row justify-content-center">
					<div className="col-lg-8">
						<div className="contact-form-wrapper">
							<p className="section-label">Contact</p>
							<h2 className="section-title">
								Get Started <span className="highlight">Today</span>
							</h2>
							<p className="contact-subtitle">
								Fill out the form below and we'll get back to you shortly
							</p>

							<iframe
								src={`https://api.leadconnectorhq.com/widget/form/${closerFormId}`}
								style={{
									width: "100%",
									height: "1231px",
									border: "none",
									borderRadius: "0px",
								}}
								id={`inline-${closerFormId}`}
								data-layout="{'id':'INLINE'}"
								data-trigger-type="alwaysShow"
								data-trigger-value=""
								data-activation-type="alwaysActivated"
								data-activation-value=""
								data-deactivation-type="neverDeactivate"
								data-deactivation-value=""
								data-form-name="Website Form"
								data-height="1231"
								data-layout-iframe-id={`inline-${closerFormId}`}
								data-form-id={closerFormId}
								data-cookie-consent="true"
								data-cookie-consent-provider="auto"
								title="Website Form"
							></iframe>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

// ---------------------------------------------------------------------------
// EmailJS-backed "Get Started" enquiry form. Writes to the public `enquiries`
// collection (tagged with the location) and sends a location-aware EmailJS
// notification. Used by locations with enquiryType: 'enquiry' (e.g. Ebbsfleet)
// that don't yet have their own Closer form, so leads are attributed correctly.
// ---------------------------------------------------------------------------
const EnquiryContactForm = ({ location }) => {
	const locationId = location?.id || "sidcup";
	const locationName = location?.name || "Urban 59 Sidcup";

	// Which fields to show. If a location defines enquiryFields, only those are
	// rendered (firstName/lastName/email always shown). Otherwise show the full
	// membership-style form. Ebbsfleet (class packs) uses name/email/phone only.
	const allowedFields = location?.enquiryFields || null;
	const showField = (field) => !allowedFields || allowedFields.includes(field);

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		sessionsPerWeek: "",
		motivation: "",
		readiness: "",
		callTime: "",
	});
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		try {
			await addDoc(collection(db, "enquiries"), {
				...formData,
				locationId,
				locationName,
				status: "new",
				createdAt: new Date().toISOString(),
			});
			// Location-aware email notification (non-blocking).
			sendEnquiryNotification({ ...formData, locationId, locationName });
			setSubmitted(true);
		} catch (error) {
			console.error("Enquiry submission error:", error);
			alert("Something went wrong. Please try again.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section id="contact" className="contact-section">
			<div className="container">
				<div className="row justify-content-center">
					<div className="col-lg-8">
						<div className="contact-form-wrapper">
							{submitted ? (
								<div style={{ textAlign: "center", padding: "3rem 1rem" }}>
									<h2 className="section-title" style={{ color: "#1a1a1a" }}>
										Thank <span className="highlight">You</span>
									</h2>
									<p
										style={{ fontSize: "1.1rem", color: "rgba(26,26,26,0.6)" }}
									>
										Thanks {formData.firstName}! We've received your enquiry and
										will be in touch soon.
									</p>
								</div>
							) : (
								<>
									<p className="section-label">Contact</p>
									<h2 className="section-title">
										Get Started <span className="highlight">Today</span>
									</h2>
									<p className="contact-subtitle">
										Fill out the form below and we'll get back to you shortly
									</p>

									<form onSubmit={handleSubmit} className="contact-form">
										<div className="form-section">
											<h3 className="form-section-title">Name</h3>
											<div className="row">
												<div className="col-md-6">
													<div className="form-group">
														<label htmlFor="en-firstName">
															First Name{" "}
															<span className="required">(required)</span>
														</label>
														<input
															type="text"
															id="en-firstName"
															name="firstName"
															value={formData.firstName}
															onChange={handleChange}
															required
															className="form-control"
														/>
													</div>
												</div>
												<div className="col-md-6">
													<div className="form-group">
														<label htmlFor="en-lastName">
															Last Name{" "}
															<span className="required">(required)</span>
														</label>
														<input
															type="text"
															id="en-lastName"
															name="lastName"
															value={formData.lastName}
															onChange={handleChange}
															required
															className="form-control"
														/>
													</div>
												</div>
											</div>
										</div>

										<div className="form-group">
											<label htmlFor="en-email">
												Email <span className="required">(required)</span>
											</label>
											<input
												type="email"
												id="en-email"
												name="email"
												value={formData.email}
												onChange={handleChange}
												required
												className="form-control"
											/>
										</div>

										{showField("phone") && (
											<div className="form-group">
												<label htmlFor="en-phone">
													Phone <span className="required">(required)</span>
												</label>
												<input
													type="tel"
													id="en-phone"
													name="phone"
													value={formData.phone}
													onChange={handleChange}
													required
													className="form-control"
												/>
											</div>
										)}

										{showField("sessionsPerWeek") && (
											<div className="form-group">
												<label htmlFor="en-sessionsPerWeek">
													On average, how many sessions per week could you
													attend? <span className="required">(required)</span>
												</label>
												<select
													id="en-sessionsPerWeek"
													name="sessionsPerWeek"
													value={formData.sessionsPerWeek}
													onChange={handleChange}
													required
													className="form-control"
												>
													<option value="">Select an option</option>
													<option value="2">2 sessions per week</option>
													<option value="3">3 sessions per week</option>
													<option value="4">4 sessions per week</option>
													<option value="5+">5+ sessions per week</option>
												</select>
											</div>
										)}

										{showField("motivation") && (
											<div className="form-group">
												<label htmlFor="en-motivation">
													Why is now the right time to make a positive change to
													your strength and fitness journey?
												</label>
												<textarea
													id="en-motivation"
													name="motivation"
													value={formData.motivation}
													onChange={handleChange}
													rows="4"
													className="form-control"
												/>
											</div>
										)}

										{showField("readiness") && (
											<div className="form-group">
												<label>
													Are you in a position to invest in your health and
													fitness? <span className="required">(required)</span>
												</label>
												<div className="radio-group">
													<label className="radio-label">
														<input
															type="radio"
															name="readiness"
															value="ready"
															checked={formData.readiness === "ready"}
															onChange={handleChange}
															required
														/>
														<span>Yes I am ready to take action</span>
													</label>
													<label className="radio-label">
														<input
															type="radio"
															name="readiness"
															value="learn-more"
															checked={formData.readiness === "learn-more"}
															onChange={handleChange}
															required
														/>
														<span>
															I'd like to learn more before committing
														</span>
													</label>
												</div>
											</div>
										)}

										{showField("callTime") && (
											<div className="form-group">
												<label htmlFor="en-callTime">
													What is the best time of day for us to give you a
													call? <span className="required">(required)</span>
												</label>
												<input
													type="text"
													id="en-callTime"
													name="callTime"
													value={formData.callTime}
													onChange={handleChange}
													required
													className="form-control"
													placeholder="e.g., Morning, Afternoon, Evening"
												/>
											</div>
										)}

										<button
											type="submit"
											className="btn-submit"
											disabled={submitting}
										>
											{submitting ? "Sending..." : "Send"}
										</button>
									</form>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

// Dispatcher: choose the enquiry UI by variant.
//   variant 'enquiry' -> EmailJS-backed, location-tagged "Get Started" form
//   default           -> Closer / LeadConnector iframe (Sidcup)
const ContactForm = ({ variant, location, formId }) => {
	if (variant === "enquiry") {
		return <EnquiryContactForm location={location} />;
	}
	return <CloserContactForm formId={formId} />;
};

export default ContactForm;

// ---------------------------------------------------------------------------
// Legacy custom React form (writes to Firestore + sends email). Preserved but
// no longer rendered. To switch back, export this instead of ContactForm.
// ---------------------------------------------------------------------------
// eslint-disable-next-line no-unused-vars
const LegacyContactForm = () => {
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		sessionsPerWeek: "",
		motivation: "",
		readiness: "",
		callTime: "",
	});
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		try {
			await addDoc(collection(db, "enquiries"), {
				...formData,
				status: "new",
				createdAt: new Date().toISOString(),
			});
			// Send email notification (non-blocking)
			sendEnquiryNotification(formData);
			setSubmitted(true);
		} catch (error) {
			console.error("Enquiry submission error:", error);
			alert("Something went wrong. Please try again.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section id="contact" className="contact-section">
			<div className="container">
				<div className="row justify-content-center">
					<div className="col-lg-8">
						<div className="contact-form-wrapper">
							{submitted ? (
								<div style={{ textAlign: "center", padding: "3rem 1rem" }}>
									<h2 className="section-title" style={{ color: "#1a1a1a" }}>
										Thank <span className="highlight">You</span>
									</h2>
									<p
										style={{ fontSize: "1.1rem", color: "rgba(26,26,26,0.6)" }}
									>
										Thanks {formData.firstName}! We've received your enquiry and
										will be in touch soon.
									</p>
								</div>
							) : (
								<>
									<p className="section-label">Contact</p>
									<h2 className="section-title">
										Get Started <span className="highlight">Today</span>
									</h2>
									<p className="contact-subtitle">
										Fill out the form below and we'll get back to you shortly
									</p>

									<form onSubmit={handleSubmit} className="contact-form">
										<div className="form-section">
											<h3 className="form-section-title">Name</h3>
											<div className="row">
												<div className="col-md-6">
													<div className="form-group">
														<label htmlFor="firstName">
															First Name{" "}
															<span className="required">(required)</span>
														</label>
														<input
															type="text"
															id="firstName"
															name="firstName"
															value={formData.firstName}
															onChange={handleChange}
															required
															className="form-control"
														/>
													</div>
												</div>
												<div className="col-md-6">
													<div className="form-group">
														<label htmlFor="lastName">
															Last Name{" "}
															<span className="required">(required)</span>
														</label>
														<input
															type="text"
															id="lastName"
															name="lastName"
															value={formData.lastName}
															onChange={handleChange}
															required
															className="form-control"
														/>
													</div>
												</div>
											</div>
										</div>

										<div className="form-group">
											<label htmlFor="email">
												Email <span className="required">(required)</span>
											</label>
											<input
												type="email"
												id="email"
												name="email"
												value={formData.email}
												onChange={handleChange}
												required
												className="form-control"
											/>
										</div>

										<div className="form-group">
											<label htmlFor="phone">
												Phone <span className="required">(required)</span>
											</label>
											<input
												type="tel"
												id="phone"
												name="phone"
												value={formData.phone}
												onChange={handleChange}
												required
												className="form-control"
											/>
										</div>

										<div className="form-group">
											<label htmlFor="sessionsPerWeek">
												On average, how many sessions per week could you attend?{" "}
												<span className="required">(required)</span>
											</label>
											<select
												id="sessionsPerWeek"
												name="sessionsPerWeek"
												value={formData.sessionsPerWeek}
												onChange={handleChange}
												required
												className="form-control"
											>
												<option value="">Select an option</option>
												<option value="2">2 sessions per week</option>
												<option value="3">3 sessions per week</option>
												<option value="4">4 sessions per week</option>
												<option value="5+">5+ sessions per week</option>
											</select>
										</div>

										<div className="form-group">
											<label htmlFor="motivation">
												Why is now the right time to make a positive change to
												your strength and fitness journey?
											</label>
											<textarea
												id="motivation"
												name="motivation"
												value={formData.motivation}
												onChange={handleChange}
												rows="4"
												className="form-control"
											/>
										</div>

										<div className="form-group">
											<label>
												Are you in a position to invest in your health and
												fitness? <span className="required">(required)</span>
											</label>
											<div className="radio-group">
												<label className="radio-label">
													<input
														type="radio"
														name="readiness"
														value="ready"
														checked={formData.readiness === "ready"}
														onChange={handleChange}
														required
													/>
													<span>Yes I am ready to take action</span>
												</label>
												<label className="radio-label">
													<input
														type="radio"
														name="readiness"
														value="learn-more"
														checked={formData.readiness === "learn-more"}
														onChange={handleChange}
														required
													/>
													<span>I'd like to learn more before committing</span>
												</label>
											</div>
										</div>

										<div className="form-group">
											<label htmlFor="callTime">
												What is the best time of day for us to give you a call?{" "}
												<span className="required">(required)</span>
											</label>
											<input
												type="text"
												id="callTime"
												name="callTime"
												value={formData.callTime}
												onChange={handleChange}
												required
												className="form-control"
												placeholder="e.g., Morning, Afternoon, Evening"
											/>
										</div>

										<button
											type="submit"
											className="btn-submit"
											disabled={submitting}
										>
											{submitting ? "Sending..." : "Send"}
										</button>
									</form>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
